import { Dimensions, Platform } from "react-native";

const { width } = Dimensions.get("window");

const COLORS = {
  green: {
    50: "#EBF5E2",
    100: "#86C756",
    200: "#78C043",
    300: "#48881A",
    400: "#338B47",
    600: "#EBF5E2",
  },
  teal: {
    50: "#E9F3F6",
    100: "#3E9EA6",
    200: "#30B2BE",
    300: "#29939C",
    400: "#2C6D7B",
    500: "#145D6C",
    600: "#0B4652",
  },
  blue: {
    50: "#D8E8FF",
    100: "#43A5D7",
    150: "#3C639B",
    200: "#289DD8",
    300: "#3887C8",
    400: "#3B9EEC",
    500: "#1F97B0",
    600: "#1B365D",
    700: "#0F2544",
    800: "#D8E8FF",
  },
  purple: {
    50: "#EEEDFF",
    100: "#3C3872",
    200: "#262262",
    300: "#1B1845",
    400: "#492F45",
    500: "#6C3892",
    600: "#5C2C87",
    700: "#331450",
    800: "#EFDBFD",
    900: "#6A2C91",
  },
  red: {
    100: "#EE7474",
    150: "#ffbebe",
    200: "#B55453",
    300: "#782b2d",
    400: "#731F21",
  },
  gray: {
    100: "#f8f8f8",
    200: "#e0e0e0",
    300: "#797A7A",
    400: "#727272",
    500: "#5e5e5e",
    600: "#333333",
  },
  rose: {
    100: "#B55453",
  },
  yellow: {
    100: "#C99C37", // second
  },
  black: "#000000",
  blackOpaque: "#00000040",
  white: "#ffffff",
  lightGray: "#eeeeee",
  overlayWhite10: "rgba(255, 255, 255, 0.1)",
  loaderBg: "rgba(0, 0, 0, 0.5)",
  paymentPannelColor: "#F7F7F7",
  paymentIconColor: "#EFEFEF",
  paleGray: "#797a7a33",
  vividBlue: "#1a73e8",
  offWhite:"#f9f9fc",
  fadedWhite: "#ffffffe0",
  charcoalGray:"#575757",
  darkGray: "#1A1A1A",
  skyBlue: "#299DD8",
};

const SPACES = {
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  8: 8,
  10: 10,
  12: 12,
  13: 13,
  14: 14,
  16: 16,
  18: 18,
  20: 20,
  22: 22,
  24: 24,
  26: 26,
  28: 28,
  30: 30,
  32: 32,
  36: 36,
  40: 40,
  44: 44,
  48: 48,
  50: 50,
  56: 56,
  64: 64,
  72: 72,
  80: 80,
  96: 96,
  112: 112,
  128: 128,
} as const;

const FONTS = {
  robotoMedium: Platform.OS == "web" ? "Roboto" : "Roboto-Medium",
  robotoRegular: Platform.OS == "web" ? "Roboto" : "Roboto-Regular",
  robotoBold: Platform.OS == "web" ? "Roboto" : "Roboto-Bold",
  robotoSlabMedium: Platform.OS == "web" ? "Roboto Slab" : "RobotoSlab-Medium",
  robotoSlabRegular:
    Platform.OS == "web" ? "Roboto Slab" : "RobotoSlab-Regular",
  robotoSlabBold: Platform.OS == "web" ? "Roboto Slab" : "RobotoSlab-Bold",
} as const;

const FONT_SIZES = {
  10: 10,
  12: 12,
  14: 14,
  16: 16,
  18: 18,
  20: 20,
  22: 22,
  24: 24,
  26: 26,
  32: 32,
  40: 40,
} as const;

const FONT_WEIGHT = {
  xlight: "300",
  light: "400",
  regular: "500",
  medium: "600",
  semiBold: "700",
  bold: "bold",
};
const BREAKPOINT_VALUES = {
  320: 320,
  768: 768,
  1024: 1024,
} as const;
const isTablet = width >= BREAKPOINT_VALUES[768];
const isDesktop = width >= BREAKPOINT_VALUES[1024];

const HCPCI_COLORS = {
  // BRAND COLORS
  primary: COLORS.purple[200],
  primaryDark: COLORS.purple[300],
  primaryLight: COLORS.purple[100],
  primaryLighter: COLORS.teal[50],
  backgroundTint: null,
  backgroundGradient: null,
  accountDeletionGradient:"linear-gradient(135deg, #0C0A29, #241E79)",
  accountDeletionWebButton: "#544BDD",
  secondary: COLORS.red[400],
  secondaryLight: COLORS.red[300],
  errorRed: COLORS.red[150],

  grayLighter: COLORS.gray[100],
  alertMessage: COLORS.gray[500],
  grayLight: COLORS.gray[300],
  gray: COLORS.gray[500],

  border: COLORS.gray[200],
  banner: COLORS.purple[400],

  white: COLORS.white,
  black: COLORS.black,
  paymentPannelColor: COLORS.paymentPannelColor,
  paymentIconColor: COLORS.paymentIconColor,
  loaderBg: COLORS.loaderBg,
  seperator: COLORS.gray["200"],
  qrBackground: COLORS.blackOpaque,

  text: {
    primary: COLORS.gray[600],
    secondary: COLORS.gray[500],
    white: COLORS.white,
    error: COLORS.red[100],
  },
  button: {
    primary: COLORS.purple[200],
    secondary: COLORS.red[400],
    btnRed: COLORS.red[100],
    danger: COLORS.red[100],
    payNow: COLORS.purple[300],
    paymentContinue: COLORS.purple[200],
  },
  notification: {
    read: COLORS.black,
    unread: COLORS.blue[200],
  },
  policyStatus: {
    primary: COLORS.green[400],
    secondary: COLORS.yellow[100],
    tertiary: COLORS.red[200],
    default: COLORS.green[400],
    paymentStatus: COLORS.purple[50],
    paymentStatusColor: COLORS.purple[300],
  },
  tiles: {
    agent: COLORS.black + 90,
    squareRight: COLORS.purple[200] + 60,
  },
};

const TYPTAP_COLRS = {
  // BRAND COLORS
  primary: COLORS.green[200],
  primaryDark: COLORS.green[300],
  primaryLight: COLORS.green[100],
  primaryLighter: COLORS.green[50],
  backgroundTint: null,
  backgroundGradient: null,
  accountDeletionGradient:"linear-gradient(135deg, #053147, #3E9559)",
  accountDeletionWebButton: "#6CB038",
  secondary: COLORS.blue[200],
  secondaryLight: COLORS.blue[100],
  errorRed: COLORS.red[150],

  grayLighter: COLORS.gray[100],
  alertMessage: COLORS.gray[500],
  grayLight: COLORS.gray[300],
  gray: COLORS.gray[500],

  border: COLORS.gray[200],
  banner: COLORS.purple[400],

  white: COLORS.white,
  black: COLORS.black,
  paymentPannelColor: COLORS.paymentPannelColor,
  paymentIconColor: COLORS.paymentIconColor,
  loaderBg: COLORS.loaderBg,
  seperator: COLORS.gray["200"],
  qrBackground: COLORS.blackOpaque,

  text: {
    primary: COLORS.gray[600],
    secondary: COLORS.gray[500],
    white: COLORS.white,
    error: COLORS.red[100],
  },
  button: {
    primary: COLORS.green[200],
    secondary: COLORS.blue[200],
    btnRed: COLORS.red[100],
    danger: COLORS.red[100],
    payNow: COLORS.green[300],
    paymentContinue: COLORS.green[200],
  },
  notification: {
    read: COLORS.black,
    unread: COLORS.blue[200],
  },
  policyStatus: {
    primary: COLORS.green[400],
    secondary: COLORS.yellow[100],
    tertiary: COLORS.red[200],
    default: COLORS.green[400],
    paymentStatus: COLORS.green[600],
    paymentStatusColor: COLORS.green[300],
  },
  tiles: {
    agent: COLORS.black + 90,
    squareRight: COLORS.green[200] + 60,
  },
};

const TAILROW_COLRS = {
  // BRAND COLORS
  // background
  primary: COLORS.teal[500],
  primaryDark: COLORS.teal[600],
  primaryLight: COLORS.teal[400],
  primaryLighter: COLORS.teal[50],
  backgroundTint: null,
  backgroundGradient: null,
  accountDeletionGradient:"linear-gradient(135deg, #154646, #0A7575)",
  accountDeletionWebButton: "#208CA2",
  secondary: COLORS.teal[300],
  secondaryLight: COLORS.teal[100],
  errorRed: COLORS.red[150],

  grayLighter: COLORS.gray[100],
  alertMessage: COLORS.gray[500],
  grayLight: COLORS.gray[300],
  gray: COLORS.gray[500],

  border: COLORS.gray[200],
  banner: COLORS.purple[400],

  white: COLORS.white,
  black: COLORS.black,
  paymentPannelColor: COLORS.paymentPannelColor,
  paymentIconColor: COLORS.paymentIconColor,
  loaderBg: COLORS.loaderBg,
  seperator: COLORS.gray["200"],
  qrBackground: COLORS.blackOpaque,

  text: {
    primary: COLORS.gray[600],
    secondary: COLORS.gray[500],
    white: COLORS.white,
    error: COLORS.red[100],
  },
  button: {
    primary: COLORS.teal[200],
    secondary: COLORS.teal[300],
    btnRed: COLORS.red[100],
    danger: COLORS.red[100],
    payNow: COLORS.teal[600],
    paymentContinue: COLORS.teal[500],
  },
  notification: {
    read: COLORS.black,
    unread: COLORS.blue[200],
  },
  policyStatus: {
    primary: COLORS.green[400],
    secondary: COLORS.yellow[100],
    tertiary: COLORS.red[200],
    default: COLORS.green[400],
    paymentStatus: COLORS.teal[50],
    paymentStatusColor: COLORS.teal[600],
  },
  tiles: {
    agent: COLORS.black + 90,
    squareRight: COLORS.teal[500] + 60,
  },
};

const PRAXIS_COLORS = {
  // BRAND COLORS
  background: COLORS.purple[600] + 20,
  primary: COLORS.purple[600],
  primaryDark: COLORS.purple[700],
  primaryLight: COLORS.teal[500],
  primaryLighter: COLORS.purple[800],
  backgroundTint: COLORS.purple[900],
  backgroundGradient:
    "radial-gradient(circle at center, #6A2C91 0%, #2A0D3E 100%)",
  accountDeletionGradient:"linear-gradient(135deg, #330B4F, #5D1F89)",
  accountDeletionWebButton: "#5D1F89",
  secondary: COLORS.blue[300],
  secondaryLight: COLORS.blue[400],
  errorRed: COLORS.red[150],

  grayLighter: COLORS.gray[100],
  alertMessage: COLORS.gray[500],
  grayLight: COLORS.gray[300],
  gray: COLORS.gray[500],

  border: COLORS.gray[200],
  banner: COLORS.purple[400],

  white: COLORS.white,
  black: COLORS.black,
  paymentPannelColor: COLORS.paymentPannelColor,
  paymentIconColor: COLORS.paymentIconColor,
  loaderBg: COLORS.loaderBg,
  seperator: COLORS.gray["200"],
  qrBackground: COLORS.blackOpaque,
  text: {
    primary: COLORS.gray[600],
    secondary: COLORS.gray[500],
    white: COLORS.white,
    error: COLORS.red[100],
  },
  button: {
    primary: COLORS.blue[300],
    btnRed: COLORS.red[100],
    danger: COLORS.red[100],
    payNow: COLORS.blue[300],
    paymentContinue: COLORS.blue[300],
  },
  notification: {
    read: COLORS.black,
    unread: COLORS.blue[200],
  },
  policyStatus: {
    primary: COLORS.green[400],
    secondary: COLORS.yellow[100],
    tertiary: COLORS.red[200],
    default: COLORS.green[400],
    paymentStatus: COLORS.purple[800],
    paymentStatusColor: COLORS.purple[600],
  },
  tiles: {
    agent: COLORS.blue[300],
    squareRight: COLORS.purple[600] + 20,
  },
};

const CORE_COLORS = {
  // BRAND COLORS
  background: COLORS.purple[600] + 20,
  primary: COLORS.blue[600],
  primaryDark: COLORS.blue[700],
  primaryLight: COLORS.blue[150],
  primaryLighter: COLORS.blue[50],
  backgroundTint: COLORS.purple[900],
  // backgroundGradient:"radial-gradient(circle at center, #0F2544 0%, #3C639B 100%)",
  backgroundGradient: "linear-gradient(to bottom, #0F2544 30%, #3C639B 100%)",
  accountDeletionGradient:"linear-gradient(135deg, #1B365D, #177A8F)",
  accountDeletionWebButton: "#1F97B0",
  secondary: COLORS.blue[500],
  secondaryLight: COLORS.blue[400],
  errorRed: COLORS.red[150],

  grayLighter: COLORS.gray[100],
  alertMessage: COLORS.gray[500],
  grayLight: COLORS.gray[300],
  gray: COLORS.gray[500],

  border: COLORS.gray[200],
  banner: COLORS.purple[400],

  white: COLORS.white,
  black: COLORS.black,
  paymentPannelColor: COLORS.paymentPannelColor,
  paymentIconColor: COLORS.paymentIconColor,
  loaderBg: COLORS.loaderBg,
  seperator: COLORS.gray["200"],
  qrBackground: COLORS.blackOpaque,
  text: {
    primary: COLORS.gray[600],
    secondary: COLORS.gray[500],
    white: COLORS.white,
    error: COLORS.red[100],
  },
  button: {
    primary: COLORS.blue[500],
    btnRed: COLORS.red[100],
    danger: COLORS.red[100],
    payNow: COLORS.blue[500],
    paymentContinue: COLORS.blue[500],
  },
  notification: {
    read: COLORS.black,
    unread: COLORS.blue[200],
  },
  policyStatus: {
    primary: COLORS.green[400],
    secondary: COLORS.yellow[100],
    tertiary: COLORS.red[200],
    default: COLORS.green[400],
    paymentStatus: COLORS.blue[800],
    paymentStatusColor: COLORS.blue[600],
  },
  tiles: {
    agent: COLORS.blue[300],
    squareRight: COLORS.purple[600] + 20,
  },
};

const getBrandingColors = (brandName: string) => {
  switch (brandName) {
    case "HCPCI":
      return HCPCI_COLORS;
    case "TAILROW":
      return TAILROW_COLRS;
    case "PRAXIS":
      return PRAXIS_COLORS;
    case "CORE":
      return CORE_COLORS;
    default:
      return TYPTAP_COLRS;
  }
};

export {
  getBrandingColors,
  COLORS,
  SPACES,
  FONTS,
  FONT_SIZES,
  BREAKPOINT_VALUES,
  isTablet,
  isDesktop,
  FONT_WEIGHT,
};
